import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { RoleDTO } from "src/app/roles/Role";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { AccountAppDTO } from "../AccountApp";
import { AppService } from "../app.service";
import { find, filter } from "lodash";

@Component({
  selector: "app-account-app",
  templateUrl: "./account-app.component.html",
  styleUrls: ["./account-app.component.scss"],
})
export class AccountAppComponent implements OnInit {
  isEdit: boolean = false;
  app!: AccountAppDTO;
  platforms!: { code: number; name: string; selected: boolean }[];
  appForm!: FormGroup;
  iOSForm!: FormGroup;
  googleForm!: FormGroup;
  webForm!: FormGroup;
  demoForm!: FormGroup;
  subscription: any;
  private onComponentDestroy = new Subject();

  intervalOptions = [
    {
      value: "second",
      name: "Seconds",
    },
    {
      value: "minute",
      name: "Minutes",
    },
    {
      value: "hour",
      name: "Hours",
    },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private AppService: AppService,
    private NotificationService: NotificationService,
    private PermissionService: PermissionService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.app = this.route.snapshot.data.app;

    this.subscription = this.route.snapshot.data.subscription;

    this.isEdit = !!this.app;

    this.appForm = new FormGroup({
      appName: new FormControl(this.app?.name, [Validators.required]),
    });

    this.iOSForm = new FormGroup({
      bundleID: new FormControl(this.app?.apn_app_bundle_id),
      key: new FormControl(this.app?.apn_key),
      keyID: new FormControl(this.app?.apn_key_id),
      teamID: new FormControl(this.app?.apn_team_id),
      mode: new FormControl("sandbox"),
    });

    this.googleForm = new FormGroup({
      mesgIDKey: new FormControl(this.app?.gcm_api_key),
    });

    this.webForm = new FormGroup({
      firebaseConfig: new FormControl(this.app?.firebase_config),
      chromeConfig: new FormControl(this.app?.chrome_config),
      safariConfig: new FormControl(this.app?.safari_config),
    });

    this.demoForm = new FormGroup({
      demo_mode: new FormControl(this.app?.demo_mode),
      demo_freq_num: new FormControl(
        this.app?.demo_freq ? this.app?.demo_freq.split(" ")[0] : 1, 
        [Validators.required, Validators.min(1)]
      ),
      demo_freq_interval: new FormControl(this.app?.demo_freq != null ? find(this.intervalOptions, {value: this.app?.demo_freq.split(" ")[1]}) : find(this.intervalOptions, {value: "minute"})),
      sim_device: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'NEW_DEVICE').length,
        [Validators.required, Validators.min(0)]
      ),
      sim_reg: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'NEW_REG').length,
        [Validators.required, Validators.min(0)]
      ),
      sim_geo_enter: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'ENTER_GEO').length,
        [Validators.required, Validators.min(0)]
      ),
      sim_geo_exit: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'EXIT_GEO').length,
        [Validators.required, Validators.min(0)]
      ),
      sim_br_enter: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'ENTER_BR').length,
        [Validators.required, Validators.min(0)]
      ),
      sim_br_exit: new FormControl(
        filter(this.app?.demo_sims, (sim) => sim === 'EXIT_BR').length,
        [Validators.required, Validators.min(0)]
      ),
    });

    const existingPlatforms: number[] = this.app?.platforms ?? [];

    this.platforms = [
      {
        code: 1,
        name: "iOS",
        selected:
          existingPlatforms.includes(1) ||
          !!this.app?.apn_app_bundle_id ||
          !!this.app?.apn_key ||
          !!this.app?.apn_key_id ||
          !!this.app?.apn_team_id,
      },
      {
        code: 2,
        name: "Android",
        selected: existingPlatforms.includes(2) || !!this.app?.gcm_api_key,
      },
      {
        code: 3,
        name: "Web",
        selected:
          existingPlatforms.includes(3) ||
          !!this.app?.firebase_config ||
          !!this.app?.chrome_config ||
          !!this.app?.safari_config,
      },
    ];
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get isCreate() {
    return !this.isEdit;
  }

  get iOSSelected() {
    return this.platforms.find((platform) => platform.name === "iOS")?.selected;
  }

  get androidSelected() {
    return this.platforms.find((platform) => platform.name === "Android")
      ?.selected;
  }

  get webSelected() {
    return this.platforms.find((platform) => platform.name === "Web")?.selected;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get sims() {
    let sims: string[] = [];

    if(this.demoForm.controls.demo_mode.value) {
      if(this.demoForm.controls.sim_device.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_device.value; i++) {
          sims.push('NEW_DEVICE');
        }
      }
      if(this.demoForm.controls.sim_reg.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_reg.value; i++) {
          sims.push('NEW_REG');
        }
      }
      if(this.demoForm.controls.sim_geo_enter.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_geo_enter.value; i++) {
          sims.push('ENTER_GEO');
        }
      }
      if(this.demoForm.controls.sim_geo_exit.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_geo_exit.value; i++) {
          sims.push('EXIT_GEO');
        }
      }
      if(this.demoForm.controls.sim_br_enter.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_br_enter.value; i++) {
          sims.push('ENTER_BR');
        }
      }
      if(this.demoForm.controls.sim_br_exit.value !== 0) {
        for(let i = 0; i < this.demoForm.controls.sim_br_exit.value; i++) {
          sims.push('EXIT_BR');
        }
      }
    }

    return sims;
  }

  get accountAppDTO(): AccountAppDTO {

    

    const accountAppDTO: AccountAppDTO = {
      account_id: this.currentAccount.accountID,
      api_id: this.app?.api_id,
      api_key: this.app?.api_key,
      name: this.appForm.controls.appName.value,
      url: this.app?.url,
      id: this.app?.id,
      demo_mode: this.demoForm.controls.demo_mode.value,
      demo_freq: `${this.demoForm.controls.demo_freq_num.value} ${this.demoForm.controls.demo_freq_interval.value.value}`,
      demo_sims: this.sims,
    } as AccountAppDTO;

    // Persist selected platforms explicitly so the API knows which
    // platforms are enabled, even if platform-specific fields are empty.
    accountAppDTO.platforms = this.platforms
      .filter((platform) => platform.selected)
      .map((platform) => platform.code);

    if (this.iOSSelected) {
      accountAppDTO.apn_app_bundle_id = this.iOSForm.controls.bundleID.value;
      accountAppDTO.apn_key = this.iOSForm.controls.key.value;
      accountAppDTO.apn_key_id = this.iOSForm.controls.keyID.value;
      accountAppDTO.apn_team_id = this.iOSForm.controls.teamID.value;
    }
    if (this.androidSelected) {
      accountAppDTO.gcm_api_key = this.googleForm.controls.mesgIDKey.value;
    }
    accountAppDTO.firebase_config = this.webForm.controls.firebaseConfig.value;

    if (this.webSelected) {
      accountAppDTO.chrome_config = this.webForm.controls.chromeConfig.value;
      accountAppDTO.safari_config = this.webForm.controls.safariConfig.value;
    }

    return accountAppDTO;
  }

  get serviceLevel() {
    return this.subscription?.plan.name;
  }

  get isAccountAdmin() {
    return this.PermissionService.roles.find(
      (role: RoleDTO) => role.code === "ACCOUNT_ADMIN",
    );
  }

  get isSuper() {
    return this.SessionService.currentUserValue!.super;
  }

  cancel() {
    this.router.navigate(["list"], { relativeTo: this.route.parent });
  }

  createApp() {
    this.uiService
      .waitForEvent(
        this.AppService.createApp(
          this.currentAccount.accountID,
          this.accountAppDTO,
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (app: any) => {
          //console.log(app);
          this.NotificationService.openSuccessSnackBar("App created");
          this.router.navigate(["list"], { relativeTo: this.route.parent });
          this.AppService.getApps(this.currentAccount.accountID).subscribe((apps: any) => {
            //console.log(apps);
            this.SessionService.updateApps(apps.records);
            apps.records.forEach((a: any) => {
              if(a.id === app.id) {
                this.SessionService.setApp(a);
              }
            });
          })
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error creating app");
        },
      );
  }

  updateApp() {
    this.uiService
      .waitForEvent(
        this.AppService.updateApp(
          this.currentAccount.accountID,
          this.app!.id,
          this.accountAppDTO,
        ).pipe(
          tap(
            (res) => {
              this.NotificationService.openSuccessSnackBar("App updated");
              this.router.navigate(["list"], { relativeTo: this.route.parent });
            },
            (err) => {
              this.NotificationService.openErrorSnackBar("Error updating app");
            },
          ),
          switchMap(() =>
            this.AppService.getApps(this.currentAccount.accountID),
          ),
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (apps) => {
          this.SessionService.updateApps(apps.records);
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error retrieving apps");
        },
      );
  }

  copyToClipboard(text: string, fieldName: string) {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text).then(() => {
        this.NotificationService.openSuccessSnackBar(`${fieldName} copied to clipboard`);
      }).catch(() => {
        this.NotificationService.openErrorSnackBar(`Failed to copy ${fieldName} to clipboard`);
      });
    } else {
      this.NotificationService.openErrorSnackBar(`No ${fieldName} available to copy`);
    }
  }
}
